<template>
  <ThemeProvider>
    <div
      class="main-wrapper exposed-layout"
      :class="{
        'exposed-layout--mobile': isMobile,
        'exposed-layout--login-page': isLoginPage,
      }"
    >
      <div
        class="exposed exposed-layout__content"
        :style="{
          height: isLoginPage ? '100vh' : `calc(100vh - ${topHeight}px)`,
          backgroundImage: `url(${transformImage(getFeUrl(`/youyan-platform/png/white.png`))})`,
        }"
      >
        <ClientOnly>
          <Compatibility />
        </ClientOnly>
        <slot />
      </div>
      <div v-if="!isLoginPage" ref="topRef">
        <TopHeader @show-user-pop="handleShowUserPop"></TopHeader>
      </div>
      <Account />
      <VideoDownload />
      <ClientOnly>
        <TemplateModal />
      </ClientOnly>
      <component :is="mobileBottomUserPopComponent" ref="mobileUserRef" />
      <PublishProcess />
      <TasteAccountHint v-model:open="showCreateModal" :accounts="accountList" />
      <FloatFeatures class="pc-only exposed-layout__float-features" v-if="!isMobile" />

      <r-gdpr-cookie-consent v-if="!isChinese()" />
    </div>
  </ThemeProvider>
</template>
<script lang="ts" setup>
import ThemeProvider from '@/design/widgets/theme-provider/index.vue';
import Account from '@/features/account/index.vue';
import PublishProcess from '@/features/publish/index.vue';
import VideoDownload from '@/features/videoDownload/index.vue';
import FloatFeatures from '@rendora/common/features/float-features/index.vue';
import Compatibility from '~/components/compatibility/index.vue';
import { PageKey } from '~/constants/page';
import MobileBottomUserPop from '~/design/widgets/mobile-bottom-user-pop/index.vue';
import MobileBottomUserPopEn from '~/design/widgets/mobile-bottom-user-pop-en/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import TasteAccountHint from '~/features/tasteAccountHint/index.vue';
import TemplateModal from '~/features/template/components/modal.vue';
import { transformImage } from '~/utils/';
import { isChinese } from '~/utils/area';
import { getFeUrl } from '~/utils/url';

const topRef = ref();
const topHeight = ref(0);
const mobileUserRef = ref<{ open: () => void } | null>(null);

const route = useRoute();

const showCreateModal = useCheckCreate();
const accountList = useAccountList();
const { isMobile } = useDevice();
const { createMobileUserPopOwner, registerMobileUserPop } = useMobileUserPop();
const mobileUserPopOwnerId = createMobileUserPopOwner();

const isLoginPage = computed(() => route.name == PageKey.LOGIN);
const mobileBottomUserPopComponent = computed(() =>
  getEnv('area') === 'CN' ? MobileBottomUserPop : MobileBottomUserPopEn,
);

const handleShowUserPop = () => {
  if (isMobile.value) {
    mobileUserRef.value?.open();
  }
};

watch(
  mobileUserRef,
  value => {
    registerMobileUserPop(mobileUserPopOwnerId, value ? () => value.open() : null);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  registerMobileUserPop(mobileUserPopOwnerId, null);
});

useResizeObserver(topRef, entries => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  topHeight.value = height + 64;
});
</script>
<style lang="scss" scoped>
.exposed-layout__content {
  display: flex;
  flex-direction: column;
  background-color: var(--x-back-1);
  overflow-y: auto;
  overflow-x: hidden;
}

.exposed-layout--login-page::before {
  display: none;
}

.exposed-layout__float-features {
  position: fixed;
  bottom: 80px;
  right: 10px !important;
  z-index: var(--layer-fixed);
}
</style>
